import throttle from 'lodash/throttle';

/**
 * Manages transparent header based on first section background color and scroll position
 */
const transparentHeader = () => {
	const header = document.querySelector('.main-header');
	const mainContent = document.querySelector('#main');

	if (!header || !mainContent) {
		return;
	}

	// Get the first section within main
	const firstSection = mainContent.querySelector(
		'section:first-child, .acf-block:first-child'
	);

	if (!firstSection) {
		return;
	}

	// Check if first section has one of the dark background classes
	const hasDarkBackground =
		firstSection.classList.contains('bg-neutral-1') ||
		firstSection.classList.contains('bg-dark') ||
		firstSection.classList.contains('bg-neutral-3');

	if (!hasDarkBackground) {
		return;
	}

	// Track navigation state
	let isNavigationActive = false;

	// Track if any navigation link is active (dropdown open)
	const isAnyNavLinkActive = () => {
		const activeLinks = header.querySelectorAll(
			'.block-navigation-link.active'
		);
		return activeLinks.length > 0;
	};

	// Track if navigation search is active
	const isNavigationSearchActive = () => {
		const navigation = header.querySelector('.block-navigation');
		return navigation && navigation.classList.contains('search-active');
	};

	// Store original padding-top value
	const computedStyle = window.getComputedStyle(firstSection);
	const originalPaddingTop = parseFloat(computedStyle.paddingTop) || 0;
	let paddingAdjusted = false;

	// Get alternate logo URL from data attribute
	const alternateLogo = header.dataset.alternateLogo;
	const logoImage = header.querySelector(
		'.block-navigation__logo, .wp-block-site-logo img'
	);
	let originalLogoSrc = logoImage ? logoImage.src : null;
	let originalLogoSrcset = logoImage ? logoImage.srcset : null;

	/**
	 * Updates logo based on bg-dark state
	 * @param {boolean} isDark - Whether the header has dark background
	 */
	const updateLogo = (isDark) => {
		if (!logoImage || !alternateLogo) {
			return;
		}

		if (isDark) {
			// Store original src and srcset if not already stored
			if (!originalLogoSrc) {
				originalLogoSrc = logoImage.src;
				originalLogoSrcset = logoImage.srcset;
			}
			// Set alternate logo and clear srcset to ensure the single image is used
			logoImage.src = alternateLogo;
			logoImage.srcset = '';
		} else if (originalLogoSrc) {
			// Restore original logo and srcset
			logoImage.src = originalLogoSrc;
			if (originalLogoSrcset) {
				logoImage.srcset = originalLogoSrcset;
			}
		}
	};

	/**
	 * Adjusts padding on first section for transparent header
	 * @param {boolean} addPadding - Whether to add padding
	 */
	const adjustFirstSectionPadding = (addPadding) => {
		// Convert pixels to rems (divide by 16)
		const originalPaddingTopRem = originalPaddingTop / 16;
		const additionalPaddingRem = 4; // 64px / 16 = 4rem

		// Only adjust on screens 1200px and wider
		if (window.innerWidth < 1200) {
			if (paddingAdjusted) {
				firstSection.style.paddingTop = originalPaddingTopRem + 'rem';
				paddingAdjusted = false;
			}
			return;
		}

		if (addPadding && !paddingAdjusted) {
			// Add 4rem to existing padding
			firstSection.style.paddingTop =
				originalPaddingTopRem + additionalPaddingRem + 'rem';
			paddingAdjusted = true;
		} else if (!addPadding && paddingAdjusted) {
			// Restore original padding
			firstSection.style.paddingTop = originalPaddingTopRem + 'rem';
			paddingAdjusted = false;
		}
	};

	/**
	 * Updates header transparency based on scroll position and navigation state
	 */
	const updateHeaderTransparency = () => {
		// Don't apply bg-dark on screens smaller than 1200px
		if (window.innerWidth < 1200) {
			header.classList.remove('bg-dark');
			updateLogo(false);
			adjustFirstSectionPadding(false);
			return;
		}

		// Don't apply bg-dark if navigation is active, any nav link dropdown is open, or search is active
		if (
			isNavigationActive ||
			isAnyNavLinkActive() ||
			isNavigationSearchActive()
		) {
			header.classList.remove('bg-dark');
			updateLogo(false);
			return;
		}

		// Check if we're at the top of the page (within 5px tolerance)
		const isAtTop = window.scrollY <= 5;

		if (isAtTop) {
			header.classList.add('bg-dark');
			updateLogo(true);
		} else {
			header.classList.remove('bg-dark');
			updateLogo(false);
		}
	};

	// Listen for navigation state changes
	window.addEventListener('navigation-opened', () => {
		isNavigationActive = true;
		updateHeaderTransparency();
	});

	window.addEventListener('navigation-closed', () => {
		isNavigationActive = false;
		updateHeaderTransparency();
	});

	// Check if navigation is already active on load
	const navigationHamburger = document.querySelector(
		'.block-navigation__hamburger'
	);
	if (
		navigationHamburger &&
		navigationHamburger.classList.contains('active')
	) {
		isNavigationActive = true;
	}

	// Set initial state
	updateHeaderTransparency();
	// Add padding on initial load if conditions are met
	if (
		window.innerWidth >= 1200 &&
		window.scrollY <= 5 &&
		!isNavigationActive &&
		!isAnyNavLinkActive() &&
		!isNavigationSearchActive()
	) {
		adjustFirstSectionPadding(true);
	}

	// Watch for changes to navigation link active state and search state
	const observer = new MutationObserver(() => {
		updateHeaderTransparency();
		const shouldHavePadding =
			window.innerWidth >= 1200 &&
			window.scrollY <= 5 &&
			!isNavigationActive &&
			!isAnyNavLinkActive() &&
			!isNavigationSearchActive();
		adjustFirstSectionPadding(shouldHavePadding);
	});

	// Observe all navigation links for class changes
	const navLinks = header.querySelectorAll('.block-navigation-link');
	navLinks.forEach((link) => {
		observer.observe(link, {
			attributes: true,
			attributeFilter: ['class'],
		});
	});

	// Also observe the navigation block for search-active class changes
	const navigationBlock = header.querySelector('.block-navigation');
	if (navigationBlock) {
		observer.observe(navigationBlock, {
			attributes: true,
			attributeFilter: ['class'],
		});
	}

	// Handle scroll events with throttle for performance
	window.addEventListener('scroll', throttle(updateHeaderTransparency, 100), {
		passive: true,
	});

	// Also handle resize in case content changes
	window.addEventListener(
		'resize',
		throttle(() => {
			updateHeaderTransparency();
			// Re-adjust padding on resize
			const shouldHavePadding =
				window.innerWidth >= 1200 &&
				window.scrollY <= 5 &&
				!isNavigationActive &&
				!isAnyNavLinkActive() &&
				!isNavigationSearchActive();
			adjustFirstSectionPadding(shouldHavePadding);
		}, 100)
	);
};

export default transparentHeader;
